"use client";

import { useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
  APPLICATION_FILE_MAX_BYTES,
  APPLICATION_FILE_TYPES,
  applicationFileExtension,
  checkApplicationFile,
  type ApplicationFileExtension,
  type ApplicationFileProblem
} from "./applicationFiles.shared";

/** A file the applicant picked that passed the checks below. */
export interface PickedApplicationFile {
  file: File;
  extension: ApplicationFileExtension;
}

const ACCEPT = [
  ".pdf",
  ".docx",
  APPLICATION_FILE_TYPES.pdf,
  APPLICATION_FILE_TYPES.docx
].join(",");

const PROBLEM_KEY: Record<ApplicationFileProblem, string> = {
  type: "errorFileType",
  size: "errorFileSize",
  content: "errorFileContent",
  unsafe: "errorFileUnsafe"
};

/**
 * Check a picked file the way the backend will (`applicationFiles.shared.ts`):
 * its name and type, its size, then its bytes. Telling the applicant now
 * saves them a submission the backend would reject.
 */
export async function pickApplicationFile(
  file: File
): Promise<PickedApplicationFile | ApplicationFileProblem> {
  const extension = applicationFileExtension(file.name, file.type);
  if (!extension) return "type";
  if (file.size === 0 || file.size > APPLICATION_FILE_MAX_BYTES) return "size";
  const problem = checkApplicationFile(
    extension,
    new Uint8Array(await file.arrayBuffer())
  );
  return problem ?? { file, extension };
}

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

interface Props {
  label: string;
  hint: string;
  value: PickedApplicationFile | null;
  onChange: (value: PickedApplicationFile | null) => void;
  /** A required-field error from the form's own validation. */
  error?: string;
  disabled?: boolean;
  /** Called when a picked file fails the checks (for analytics). */
  onReject?: (problem: ApplicationFileProblem) => void;
}

const UploadIcon = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </svg>
);

const DocumentIcon = () => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </svg>
);

/**
 * One file field: a drop zone to click or drag a file onto, then a card with
 * the file and its Replace / Remove actions. The native input stays in the
 * page (visually hidden) so the field is reachable and openable by keyboard.
 */
export function ApplicationFilePicker({
  label,
  hint,
  value,
  onChange,
  error,
  disabled,
  onReject
}: Props) {
  const t = useTranslations("OpenApplicationForm");
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<ApplicationFileProblem | null>(null);
  const [checking, setChecking] = useState(false);
  const [dragging, setDragging] = useState(false);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setChecking(true);
    const result = await pickApplicationFile(file);
    setChecking(false);
    if (typeof result === "string") {
      setProblem(result);
      onChange(null);
      onReject?.(result);
    } else {
      setProblem(null);
      onChange(result);
    }
    // Let the same file be picked again after a fix or a removal.
    if (input.current) input.current.value = "";
  };

  const blocked = disabled || checking;
  const message = problem ? t(PROBLEM_KEY[problem]) : error;

  const dropHandlers = {
    onDragOver: (e: React.DragEvent) => {
      if (blocked) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (!blocked) void pick(e.dataTransfer.files?.[0]);
    }
  };

  return (
    <div className="mb-6">
      <p className="text-sm text-text-body font-body mb-2">{label}</p>
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={ACCEPT}
        className="peer sr-only"
        aria-label={label}
        disabled={blocked}
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      {value ? (
        <div
          {...dropHandlers}
          className={`flex items-center gap-4 rounded-xl border px-4 py-3 transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-bubblegum ${
            dragging ? "border-bubblegum bg-bubblegum/5" : "border-border"
          }`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bubblegum/15 text-bubblegum-text">
            <DocumentIcon />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-text-primary font-body">
              {value.file.name}
            </p>
            <p className="text-xs text-text-muted font-body">
              {formatSize(value.file.size)}
            </p>
          </div>
          <label
            htmlFor={inputId}
            className={`text-xs font-semibold font-body text-text-muted hover:text-text-primary ${
              blocked ? "pointer-events-none opacity-40" : "cursor-pointer"
            }`}
          >
            {t("replaceFile")}
          </label>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="text-xs font-semibold font-body text-text-muted hover:text-error disabled:opacity-40"
          >
            {t("removeFile")}
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          {...dropHandlers}
          className={`flex items-center gap-4 rounded-xl border border-dashed px-4 py-4 transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-bubblegum ${
            blocked ? "cursor-wait opacity-60" : "cursor-pointer"
          } ${
            dragging
              ? "border-bubblegum bg-bubblegum/5"
              : message
                ? "border-error"
                : "border-border hover:border-bubblegum hover:bg-white/[0.03]"
          }`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-text-muted">
            <UploadIcon />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-text-primary font-body">
              {checking ? t("checkingFile") : t("dropPrompt")}
            </span>
            <span className="block text-xs text-text-muted font-body">
              {hint}
            </span>
          </span>
        </label>
      )}
      {message && (
        <p role="alert" className="text-xs text-error font-body mt-1.5">
          {message}
        </p>
      )}
    </div>
  );
}
